/**
 * Cross-server "my builds" slice — owns the per-session player's saved
 * weapon-build library, fetched directly by Discord id via the
 * `myPlayerBuilds(discordUserId: Int!)` GraphQL field. Independent of the
 * active board so a player can manage their library without first joining
 * a guild's board.
 *
 * Mutations route through the new `myAddBuild` / `mySetBuild` /
 * `myRemoveBuild` / `mySetActiveBuild` / `mySetDefaultBuild` mutations — all
 * of which take a
 * Discord user id (snowflake) and operate across every guild where the
 * player has a row. The /my-builds route therefore does not require the
 * session player to currently appear on the active board; the slice just
 * needs the signed-in snowflake to identify the library.
 *
 * `myBuilds` is exposed as a flat `ReadonlyArray<ReadonlyArray<string>>`
 * — each entry is one saved build's weapon slugs — because that's the
 * shape `PlayerCard`'s builds list and `MyBuildsView` already iterate
 * without any per-build metadata. The `orderIndex` returned by the
 * backend is used to sort the rows, then discarded.
 */
import type { Ref } from 'vue'
import { ref } from 'vue'

import { resolveClient } from '@/shared/ports/resolveClient'

import {
  MyAddBuildDocument,
  MyPlayerBuildsDocument,
  MyRemoveBuildDocument,
  MySetActiveBuildDocument,
  MySetBuildDocument,
  MySetDefaultBuildDocument,
} from '../../lib/handWrittenGraphql.ts'
import type { MySetDefaultBuildMutation } from '../../lib/handWrittenGraphql.ts'

// Local alias — see `stores/board/actions.ts` for the rationale.
const apolloClient = resolveClient()

/**
 * Context slice needed by `useMyBuilds`. Deliberately minimal — the
 * earlier implementation threaded a bag of player-action callbacks
 * (and the live `players` ref) so mutations could delegate to the
 * live board's `addBuild` / `removeBuild` / etc. The new `my*Build*`
 * mutations key directly off the Discord user id, so all that
 * plumbing is now unnecessary. Keeping the surface small lets both
 * the slim composer and the unit tests wire in a one-line context.
 */
export interface MyBuildsCtx {
  /** The session's Discord user id, or `'0'` when not signed in. */
  readonly discordUserId: Ref<string>
}

export interface MyBuildsApi {
  /**
   * The session player's saved cross-server builds, ordered by
   * `orderIndex` ascending. Each entry is the build's weapon slugs as a
   * `ReadonlyArray<string>` so consumers can iterate directly without
   * reaching into a per-row metadata object.
   */
  readonly builds: Ref<ReadonlyArray<ReadonlyArray<string>>>
  /** True while `fetchMyBuilds` is in flight. */
  readonly loading: Ref<boolean>
  /** Last `fetchMyBuilds` / mutation error, or `null` if clean. */
  readonly error: Ref<string | null>
  /**
   * Fetch the session player's saved builds from the backend. No-op when
   * the session is signed out (`discordUserId === '0'`); clears any prior
   * error on entry and writes the new one on failure.
   */
  readonly fetchMyBuilds: () => Promise<void>
  /**
   * Append a new build via the `myAddBuild(discordUserId, weapons)`
   * mutation, then re-fetch `myPlayerBuilds` so the UI mirrors the new
   * state. The caller supplies the two-weapon payload — the picker in
   * `MyBuildsView` always produces one; we don't fall back to a
   * "mainWeapons seed" because the discord-keyed surface has no notion
   * of a live player row.
   */
  readonly addMyBuild: (weapons: ReadonlyArray<string>) => Promise<void>
  /** Replace the build at `index` via `mySetBuild(discordUserId, index, weapons)`. */
  readonly setMyBuild: (
    index: number,
    weapons: ReadonlyArray<string>,
  ) => Promise<void>
  /** Remove the build at `index` via `myRemoveBuild(discordUserId, index)`. */
  readonly removeMyBuild: (index: number) => Promise<void>
  /** Mark the build at `index` as active via `mySetActiveBuild(discordUserId, index)`. */
  readonly setMyActiveBuild: (index: number) => Promise<void>
  /** Move the build at `index` to slot 1, making it the cross-server default. */
  readonly setMyDefaultBuild: (index: number) => Promise<void>
}

export function useMyBuilds(ctx: MyBuildsCtx): MyBuildsApi {
  const builds = ref<ReadonlyArray<ReadonlyArray<string>>>([])
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)

  async function fetchMyBuilds(): Promise<void> {
    const me = ctx.discordUserId.value
    if (me === '' || me === '0') {
      builds.value = []
      error.value = null
      return
    }
    loading.value = true
    error.value = null
    try {
      const res = await apolloClient.query({
        query: MyPlayerBuildsDocument,
        variables: { discordUserId: me },
        fetchPolicy: 'network-only',
      })
      const data = res.data as { myPlayerBuilds: ReadonlyArray<ReadonlyArray<string>> } | null
      // Backend returns `[[String!]!]!` — each inner array is one build's
      // weapon slugs, already ordered by `order_index` ASC. The flat
      // shape matches what PlayerCard / MyBuildsView iterate, so no
      // projection is needed; we just trust the server order.
      builds.value = data?.myPlayerBuilds ?? []
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
    } finally {
      loading.value = false
    }
  }

  async function addMyBuild(weapons: ReadonlyArray<string>): Promise<void> {
    const me = ctx.discordUserId.value
    if (me === '' || me === '0') {
      error.value = 'Sign in to manage your builds.'
      return
    }
    error.value = null
    try {
      await apolloClient.mutate({
        mutation: MyAddBuildDocument,
        variables: { discordUserId: me, weapons },
      })
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
      return
    }
    await fetchMyBuilds()
  }

  async function setMyBuild(
    index: number,
    weapons: ReadonlyArray<string>,
  ): Promise<void> {
    const me = ctx.discordUserId.value
    if (me === '' || me === '0') {
      error.value = 'Sign in to manage your builds.'
      return
    }
    error.value = null
    try {
      await apolloClient.mutate({
        mutation: MySetBuildDocument,
        variables: { discordUserId: me, index, weapons },
      })
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
      return
    }
    await fetchMyBuilds()
  }

  async function removeMyBuild(index: number): Promise<void> {
    const me = ctx.discordUserId.value
    if (me === '' || me === '0') {
      error.value = 'Sign in to manage your builds.'
      return
    }
    error.value = null
    try {
      await apolloClient.mutate({
        mutation: MyRemoveBuildDocument,
        variables: { discordUserId: me, index },
      })
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
      return
    }
    await fetchMyBuilds()
  }

  async function setMyActiveBuild(index: number): Promise<void> {
    const me = ctx.discordUserId.value
    if (me === '' || me === '0') {
      error.value = 'Sign in to manage your builds.'
      return
    }
    error.value = null
    try {
      await apolloClient.mutate({
        mutation: MySetActiveBuildDocument,
        variables: { discordUserId: me, index },
      })
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
      return
    }
    await fetchMyBuilds()
  }

  async function setMyDefaultBuild(index: number): Promise<void> {
    const me = ctx.discordUserId.value
    if (me === '' || me === '0') {
      error.value = 'Sign in to manage your builds.'
      return
    }
    if (index <= 0) return
    error.value = null
    try {
      const result = await apolloClient.mutate({
        mutation: MySetDefaultBuildDocument,
        variables: { discordUserId: me, index },
      })
      const data = result.data as MySetDefaultBuildMutation | null | undefined
      const ordered = data?.mySetDefaultBuild
      if (ordered === undefined) {
        error.value = 'The server did not return the updated build order.'
        return
      }
      builds.value = ordered.map((build) => [...build])
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : String(e)
    }
  }

  return {
    builds,
    loading,
    error,
    fetchMyBuilds,
    addMyBuild,
    setMyBuild,
    removeMyBuild,
    setMyActiveBuild,
    setMyDefaultBuild,
  }
}
